import "vue/jsx";

import { createApp } from "vue";
import "./../index.css";
import { App } from "./App";
import { router } from "./router";
import { restaurantSettingsAPI } from "@app/data/api/restaurant_settings";
import { serverRouting } from "@app/data/helpers";
import * as Sentry from "@sentry/vue";
import { isPackaged } from "@lemmonhq/orders";
import { VueQueryPlugin, QueryClient } from "@tanstack/vue-query";
import { release, lemmonEnv } from "../version";

// Create Vue Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: "always",
      refetchOnWindowFocus: false,
    },
  },
});
const ignoredTelemetryUrls =
  /(?:api2\.amplitude\.com|api-sr\.amplitude\.com|amplitude\.com|posthog\.com|eu\.i\.posthog\.com|mixpanel\.com|api-eu\.mixpanel\.com|\/cdn-cgi\/rum)/i;

const app = createApp(App);
if (import.meta.env.VITE_SENTRY_DSN) {
  const dist = lemmonEnv
    ? new URLSearchParams({
        version: lemmonEnv?.app?.version || "none",
        build: lemmonEnv?.app?.build || "none",
        runtimeVersion: lemmonEnv?.app?.runtimeVersion || "none",
        ota: lemmonEnv?.ota?.createdAt || "none",
      }).toString()
    : "untagged";
  Sentry.setTag("container_version", dist);
  Sentry.init({
    app,
    ignoreErrors: [
      /mixpanel/i,
      /amplitude/i,
      /window.webkit.messageHandlers/gi,
      /RPC request timed out/gi,
      "Failed to fetch",
      "Load failed",
      "TypeError: Failed to fetch",
      "TypeError: NetworkError when attempting to fetch resource.",
      "Cashier has in progress payment. Please close this one first!",
      "Failed to abort transaction",
      "Not authenticated",
      // Expected table-credit answers the POS handles (e.g. closing a table
      // with credit left opens the settlement dialog). Same list as the
      // server's in entrypoints/wrangler.ts and instrument.mjs.
      "table_credit_settlement_required",
      "table_credit_payment_pending",
      "table_credit_settle_amount_changed",
      "table_credit_insufficient",
      /NATIVE_REQUEST_FAILED/i,
      /nativeFetchBridge/gi,
      /The I\/O read operation failed/gi,
      /Connection to Indexed Database server lost. Refresh the page to try again/gi,
    ],
    tracePropagationTargets: [/^\/api\//],
    dsn: import.meta.env.VITE_SENTRY_DSN,
    release,
    dist: dist,
    integrations: [
      Sentry.httpContextIntegration(),
      Sentry.httpClientIntegration(),
      //      Sentry.browserProfilingIntegration(),
      Sentry.browserSessionIntegration(),
      Sentry.dedupeIntegration(),
      Sentry.extraErrorDataIntegration(),
      Sentry.browserTracingIntegration({
        router,
        shouldCreateSpanForRequest: (url) => {
          if (
            /\/cdn-cgi\/rum/i.test(url) ||
            /posthog\.com/i.test(url) ||
            /amplitude/i.test(url) ||
            /mixpanel/i.test(url) ||
            /\/assets\/.*\.json$/i.test(url)
          ) {
            return false;
          }
          const lowSampleRoutes = [
            /\/api\/trpc\/shop\.getUpdates/i,
            /\/api\/trpc\/shop\.getSnapshot/i,
            /\/api\/tables/i,
            /\/api\/orders\/active/i,
          ];
          if (lowSampleRoutes.some((re) => re.test(url))) {
            return Math.random() < 0.1;
          }
          return true;
        },
        enableLongAnimationFrame: false,
        enableLongTask: false,
        ignoreResourceSpans: [
          "resource.css",
          "resource.script",
          "resource.link",
          "resource.img",
          "resource.other",
        ],
      }),
      //      Sentry.onUnhandledRejectionIntegration({ mode: "none" }),
      Sentry.linkedErrorsIntegration(),
      //      Sentry.captureConsoleIntegration({
      //        levels: ["error"],
      //      }),
    ].filter((el) => el) as any,
    beforeBreadcrumb(breadcrumb) {
      const url = breadcrumb.data?.url;
      const message = breadcrumb.message || "";

      if (typeof url === "string" && ignoredTelemetryUrls.test(url)) {
        return null;
      }

      if (/Amplitude Logger|PostHog|Mixpanel/i.test(message)) {
        return null;
      }

      return breadcrumb;
    },

    beforeSend(event) {
      const values = event.exception?.values || [];
      const errorText = values
        .map((v) => `${v.type || ""} ${v.value || ""}`)
        .join(" ");

      // Drop telemetry/network noise
      if (/Amplitude Logger|PostHog|Mixpanel/i.test(errorText)) {
        return null;
      }

      // Drop events whose breadcrumbs are only/mostly analytics failures
      const breadcrumbs = event.breadcrumbs || [];
      const hasIgnoredTelemetryBreadcrumb = breadcrumbs.some((b) => {
        const url = b.data?.url;
        return typeof url === "string" && ignoredTelemetryUrls.test(url);
      });

      return event;
    },

    sendDefaultPii: true,
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: import.meta.env.VITE_ENV === "staging" ? 0 : 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

app.use(VueQueryPlugin, { queryClient });
app.use(router);
serverRouting.start();
app.mount("#root");

// Test helper — only exposed in non-production for Playwright E2E tests.
if (import.meta.env.VITE_ENV !== "production") {
  (window as any).testAPI = {
    updateSetting: (key: string, value: unknown) =>
      restaurantSettingsAPI.updateSetting(key, {
        value: JSON.stringify(value),
        value_type: typeof value === "boolean" ? "boolean" : "json",
      }),
  };
}

// so on packaged app we get native scroll behaviour
if (isPackaged) {
  const styleString = `html, body {  overscroll-behavior: none; overflow: hidden; }`;
  const style = document.createElement("style");
  style.textContent = styleString;
  (document as any).head.append(style);
}
//(window as any).Intercom("boot", {
//  api_base: "https://api-iam.intercom.io",
//  app_id: "gvqus1cx",
//});
